import { TimelineEntry, TimelineStatus } from '~/app/pages/Transfers/_components/Timeline/types';
import { buildTransferParty } from '~/app/pages/Transfers/_utils/timeline';
import { TransferStatus } from '~/lib/transfers';
import { BookTransfer } from '~/repositories';
import { AccountNumber, BankAccount } from '~/repositories/BankAccountRepository';

const bookEvents = [
  {
    label: 'Created',
    field: 'createdAt',
  },
  {
    label: 'Pending Approval',
    field: 'preReviewAt',
  },
  {
    label: 'Initiated',
    field: 'initiatedAt',
  },
  {
    label: 'Pending Submission',
    field: 'pendingSubmissionAt',
    status: TimelineStatus.Pending,
  },
  {
    label: 'Submitted',
    field: 'submittedAt',
  },
  {
    label: 'Completed',
    field: 'completedAt',
    status: TimelineStatus.Success,
  },
  {
    label: 'Rejected',
    field: 'rejectedAt',
    status: TimelineStatus.Danger,
  },
];

export const buildBookEvents = ({
  transfer,
  senderAccount,
  receiverAccount,
}: {
  transfer: BookTransfer & { isIncoming?: boolean; reviews?: any[] };
  senderAccount: {
    senderAccountNumber: AccountNumber;
    senderBankAccount: BankAccount;
    senderEntityType?: 'person' | 'business';
  };
  receiverAccount: {
    receiverAccountNumber: AccountNumber;
    receiverBankAccount: BankAccount;
    receiverEntityType?: 'person' | 'business';
  };
}): TimelineEntry[] => {
  const status = transfer.status.toLowerCase() as TransferStatus;
  const rejected = status === TransferStatus.REJECTED;
  const completed = status === TransferStatus.COMPLETED;
  const canceled = status === TransferStatus.CANCELED;
  const pendingCompletion = !completed && !rejected && !canceled;

  const events = bookEvents
    .filter(
      (event) =>
        event.field === 'createdAt' ||
        (event.field === 'completedAt' && (completed || pendingCompletion)) ||
        (!rejected && !!transfer[event.field as keyof typeof transfer])
    )
    .map((event) => {
      let amount;
      if (transfer.amount) {
        amount = {
          value: typeof transfer.amount === 'string' ? parseFloat(transfer.amount) : transfer.amount,
          isAdding: event.field === 'completedAt' || (completed && event.label === 'Completed'),
          currencyCode: 'USD',
        };
      }

      let date;
      if (event.field === 'completedAt' && completed && !transfer[event.field as keyof typeof transfer]) {
        date = transfer.updatedAt ? new Date(transfer.updatedAt) : 'Completed';
      } else {
        date = (transfer[event.field as keyof typeof transfer] as string | undefined)
          ? new Date(transfer[event.field as keyof typeof transfer] as string)
          : undefined;
      }

      // For book transfers, we only need to show the sender account when creating and receiver when completing
      const isCreated = event.field === 'createdAt';
      const isComplete = event.field === 'completedAt' || (completed && event.label === 'Completed');
      const isCreatedOrComplete = isCreated || isComplete;

      const partyData = isCreatedOrComplete
        ? buildTransferParty({
            account: isCreated
              ? {
                  accountNumber: senderAccount.senderAccountNumber,
                  bankAccount: senderAccount.senderBankAccount,
                  entityType: senderAccount.senderEntityType,
                  isBookTransfer: true,
                }
              : {
                  accountNumber: receiverAccount.receiverAccountNumber,
                  bankAccount: receiverAccount.receiverBankAccount,
                  entityType: receiverAccount.receiverEntityType,
                  isBookTransfer: true,
                },
            isIncoming: !!transfer.isIncoming,
            isOriginator: isCreated,
          })
        : undefined;

      let eventStatus;
      if (event.field === 'completedAt') {
        eventStatus = completed ? TimelineStatus.Success : TimelineStatus.Pending;
      } else {
        const defaultStatus = rejected ? TimelineStatus.Danger : TimelineStatus.Pending;
        eventStatus = !!transfer[event.field as keyof typeof transfer] ? event.status : defaultStatus;
      }

      return {
        label: event.label,
        amount,
        date,
        partyData,
        status: eventStatus,
      };
    });

  if (rejected) {
    events.push({
      label: 'Rejected',
      amount: transfer.amount
        ? {
            value: typeof transfer.amount === 'string' ? parseFloat(transfer.amount) : transfer.amount,
            isAdding: false,
            currencyCode: 'USD',
          }
        : undefined,
      date: transfer.updatedAt ? new Date(transfer.updatedAt) : 'Rejected',
      status: TimelineStatus.Danger,
      partyData: buildTransferParty({
        account: {
          accountNumber: senderAccount.senderAccountNumber,
          bankAccount: senderAccount.senderBankAccount,
          entityType: senderAccount.senderEntityType,
          isBookTransfer: true,
        },
        isIncoming: !!transfer.isIncoming,
        isOriginator: false,
      }),
    });
  }

  return events;
};
